import ApiService from './api.service';

export type SupersetRecommendationRunStatus = 'RUNNING' | 'COMPLETE' | 'FAILED';
export type SupersetRecommendationStatus = 'PROPOSED' | 'ACCEPTED' | 'DISMISSED';

export interface SupersetRecommendationRun {
  id: string;
  status: SupersetRecommendationRunStatus;
  error?: string | null;
  requested_by?: string | null;
  started_at: string;
  completed_at?: string | null;
  workload_branches?: string[];
  lookback_days?: number | null;
  max_dimensions_per_superset: number;
  max_supersets: number;
  min_analyses_per_superset: number;
  total_analyses: number;
  eligible_analyses: number;
  already_covered_analyses: number;
  ineligible_analyses: number;
  recommended_covered_analyses: number;
  ineligible_reasons: Record<string, number>;
}

export interface SupersetRecommendation {
  id: string;
  run_id: string;
  status: SupersetRecommendationStatus;
  metric_ids: string[];
  dimension_ids: string[];
  filter_dimension_ids: string[];
  metric_names: string[];
  dimension_names: string[];
  covered_analysis_ids: string[];
  covered_analysis_names: string[];
  covered_analysis_count: number;
  coverage_pct_of_eligible: number;
  pipeline_node_id?: string | null;
  accepted_at?: string | null;
  accepted_by?: string | null;
  dismissed_at?: string | null;
  dismissed_by?: string | null;
}

export type SupersetSource = 'DASHBOARD' | 'RECOMMENDATION';
export type SupersetFreshness = 'FRESH' | 'STALE' | 'NEVER_BUILT';

export interface ActiveSuperset {
  id: string;
  source: SupersetSource;
  pipeline_node_id?: string | null;
  node_name?: string | null;
  node_label?: string | null;
  node_missing: boolean;
  dashboard_id?: string | null;
  served_label?: string | null;
  recommendation_id?: string | null;
  metric_ids: string[];
  metric_names: string[];
  dimension_ids: string[];
  dimension_names: string[];
  filter_dimension_ids: string[];
  hll_metric_ids: string[];
  hll_metric_names: string[];
  covered_analysis_count?: number | null;
  last_built_at?: string | null;
  freshness: SupersetFreshness;
}

export interface StartRunParams {
  lookback_days?: number | null;
  max_dimensions_per_superset?: number;
  max_supersets?: number;
  min_analyses_per_superset?: number;
  workload_branches?: string[];
}

export class SupersetRecommendationService {
  async startRun(params: StartRunParams): Promise<SupersetRecommendationRun> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/superset/runs',
      params,
    );
    return (result as { record: SupersetRecommendationRun }).record;
  }

  async listRuns(): Promise<SupersetRecommendationRun[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/superset/runs',
    );
    return (result as { records: SupersetRecommendationRun[] }).records ?? [];
  }

  async getRun(runId: string): Promise<{
    record: SupersetRecommendationRun;
    recommendations: SupersetRecommendation[];
  }> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/superset/runs/${runId}`,
    );
    return result as {
      record: SupersetRecommendationRun;
      recommendations: SupersetRecommendation[];
    };
  }

  async listActiveSupersets(): Promise<ActiveSuperset[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/superset/active',
    );
    return (result as { records: ActiveSuperset[] }).records ?? [];
  }

  async accept(recommendationId: string): Promise<SupersetRecommendation> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/superset/${recommendationId}/accept`,
    );
    return (result as { record: SupersetRecommendation }).record;
  }

  async dismiss(recommendationId: string): Promise<SupersetRecommendation> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/superset/${recommendationId}/dismiss`,
    );
    return (result as { record: SupersetRecommendation }).record;
  }

  async delete(recommendationId: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/superset/${recommendationId}`,
    );
  }
}

export const supersetRecommendationService = new SupersetRecommendationService();
